if (!customElements.get('launch-campaign-hero')) {
  customElements.define('launch-campaign-hero', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.buttons = [...this.querySelectorAll('[data-select]')];
      this.videos = [...this.querySelectorAll('video')];
      this.cues = [...this.querySelectorAll('[data-cue]')];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.index = 0;
      this.paused = this.motion.matches || this.dataset.autoplay === 'false';
      this.hovered = false;
      this.visible = false;
      this.failed = new WeakSet();
      this.querySelector('.launch-hero__controls').hidden = false;
      this.buttons.forEach(button => button.addEventListener('click', () => {
        this.paused = true;
        this.show(Number(button.dataset.select), true);
      }, options));
      this.querySelector('[data-toggle]').addEventListener('click', () => {
        this.paused = !this.paused;
        this.sync();
      }, options);
      this.querySelectorAll('.launch-hero__copy, .launch-hero__selectors').forEach(area => {
        area.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') { this.hovered = true; this.sync(); }
        }, options);
        area.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, options);
      });
      this.addEventListener('focusin', () => this.sync(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => { if (this.isConnected) this.sync(); }), options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => { this.paused = this.motion.matches || this.dataset.autoplay === 'false'; this.sync(); }, options);
      document.addEventListener('shopify:block:select', event => {
        if (!window.Shopify?.designMode) return;
        const cue = this.cues.find(item => item.dataset.blockId === event.detail.blockId);
        if (!cue) return;
        if (!this.editorCue) this.editorWasPaused = this.paused;
        this.editorCue = cue;
        this.paused = true;
        this.show(0);
        const video = this.activeVideo;
        if (!video || this.motion.matches) return;
        const preview = () => {
          if (this.editorCue !== cue || !this.isConnected) return;
          const start = Number(cue.dataset.start);
          const end = Number(cue.dataset.end);
          video.currentTime = Math.max(0, Math.min(start + .3, (start + end) / 2, video.duration - .05));
          video.classList.add('is-playing');
          this.updateFilm();
        };
        if (video.readyState >= 1) preview();
        else {
          video.addEventListener('loadedmetadata', preview, { ...options, once: true });
          video.load();
        }
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        if (this.editorCue?.dataset.blockId !== event.detail.blockId) return;
        this.editorCue = null;
        this.paused = this.motion.matches || this.editorWasPaused;
        this.sync();
      }, options);
      this.mobile.addEventListener('change', () => this.sync(), options);
      this.videos.forEach(video => {
        video.muted = true;
        video.loop = false;
        video.addEventListener('ended', () => {
          if (this.index === 0 && this.canRun()) this.show(1);
        }, options);
        video.addEventListener('error', () => { this.failed.add(video); video.classList.remove('is-playing'); this.sync(); }, options);
        video.addEventListener('playing', () => { clearTimeout(this.timer); video.classList.add('is-playing'); this.updateFilm(); }, options);
        ['seeked', 'timeupdate', 'loadeddata', 'pause'].forEach(event => video.addEventListener(event, () => this.updateFilm(), options));
        video.addEventListener('waiting', () => {
          if (this.index === 0 && this.canRun()) this.schedule(this.dataset.formSeconds);
        }, options);
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: 0 });
      this.observer.observe(this);
      this.show(0);
      this.updateFilm();
    }
    updateFilm() {
      cancelAnimationFrame(this.filmFrame);
      const video = this.activeVideo;
      const enabled = this.dataset.filmText === 'true' && this.index === 0 && video && !this.motion.matches && !this.failed.has(video) && video.readyState >= 2 && video.classList.contains('is-playing');
      this.classList.toggle('has-film-text', Boolean(enabled));
      const time = enabled ? video.currentTime : -1;
      this.cues.forEach(cue => {
        const start = Number(cue.dataset.start);
        const end = Number(cue.dataset.end);
        const opacity = time >= start && time < end ? Math.max(0, Math.min(1, (time - start) / .2, (end - time) / .25)) : 0;
        cue.style.opacity = opacity;
      });
      const hold = Number(this.dataset.blurHold || 1);
      const clear = Math.max(hold + .1, Number(this.dataset.blurClear || 1.8));
      const blur = enabled ? Number(this.dataset.introBlur || 0) * Math.max(0, Math.min(1, (clear - time) / (clear - hold))) : 0;
      this.style.setProperty('--film-blur', `${blur}px`);
      this.style.setProperty('--film-scale', blur > 0 ? '1.035' : '1');
      if (enabled && !video.paused && this.visible && !document.hidden) this.filmFrame = requestAnimationFrame(() => this.updateFilm());
    }
    canRun() {
      const focused = document.activeElement;
      const interacting = this.contains(focused) && !focused.closest('[data-toggle]');
      return this.isConnected && this.visible && !document.hidden && !this.paused && !this.hovered && !interacting;
    }
    show(index, announce = false) {
      clearTimeout(this.timer);
      if (index !== this.index) this.videos.forEach(video => { video.pause(); video.currentTime = 0; video.classList.remove('is-playing'); });
      this.index = index;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.inert = i !== index;
        slide.setAttribute('aria-hidden', String(i !== index));
      });
      this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      if (announce) this.querySelector('[data-status]').textContent = index === 0 ? 'Form Series 01' : 'The Element Set';
      this.sync();
    }
    schedule(seconds) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { if (this.canRun()) this.show(this.index === 0 ? 1 : 0); }, Number(seconds) * 1000);
    }
    sync() {
      clearTimeout(this.timer);
      const toggle = this.querySelector('[data-toggle]');
      toggle.setAttribute('aria-label', this.paused ? 'Play hero rotation' : 'Pause hero rotation');
      this.querySelector('[data-toggle-symbol]').textContent = this.paused ? '▶' : 'Ⅱ';
      const mobileVideo = this.querySelector('[data-video-mobile] video');
      const video = this.mobile.matches && mobileVideo ? mobileVideo : this.querySelector('[data-video-desktop] video');
      this.activeVideo = video;
      this.videos.forEach(item => {
        item.parentElement.hidden = item !== video || this.motion.matches;
        if (item !== video || this.index !== 0 || !this.canRun() || this.motion.matches) item.pause();
      });
      this.updateFilm();
      if (!this.canRun()) return;
      if (this.index === 0 && video && !this.failed.has(video) && !this.motion.matches) {
        if (video.ended) { this.show(1); return; }
        // A stalled or blocked video must never trap visitors on the first slide.
        this.schedule(this.dataset.formSeconds);
        video.play().then(() => {
          if (this.index === 0 && this.canRun()) clearTimeout(this.timer);
          else video.pause();
        }).catch(() => {
          if (this.index !== 0 || !this.canRun()) return;
          this.failed.add(video);
          video.classList.remove('is-playing');
          this.schedule(this.dataset.formSeconds);
        });
      } else {
        this.schedule(this.index === 0 ? this.dataset.formSeconds : this.dataset.stillSeconds);
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.filmFrame);
      this.abort.abort();
      this.observer.disconnect();
      this.videos.forEach(video => video.pause());
    }
  });
}
